import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, MessageSquare, DollarSign, Pause, Link2, Activity } from "lucide-react";
import { api, type AdminUser } from "../../../api";
import KpiCard from "../../../components/charts/KpiCard";
import SystemPulse from "../../../components/charts/SystemPulse";
import { fadeUp, stagger } from "../../../lib/motion";
import { fmtUsd, fmtNum, fmtRelativeTime } from "../../../lib/format";

/**
 * Two layers: user-derived activity from /api/admin/users (top), and the
 * CloudWatch-derived System pulse (bottom — see SystemPulse component).
 * The user-side numbers stay the same on a healthy day even when the
 * underlying infra is on fire, so we read both at once.
 */
export default function Health() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const all: AdminUser[] = [];
        let cursor: string | undefined;
        for (let i = 0; i < 5; i++) {
          const res = await api.listUsers(cursor);
          all.push(...res.users);
          if (!res.next_cursor) break;
          cursor = res.next_cursor;
        }
        setUsers(all);
      } catch (e: unknown) {
        setErr(e instanceof Error ? e.message : "load failed");
      }
    })();
  }, []);

  if (err) return <div className="text-terracotta">{err}</div>;
  if (!users)
    return (
      <div>
        <div className="font-serif italic text-ink-soft">admin · system health</div>
        <h2 className="display text-4xl md:text-5xl mt-2">Infrastructure.</h2>
        <p className="mt-10 text-ink-faint">Loading…</p>
      </div>
    );

  const now = Date.now() / 1000;
  const hour = 3600;
  const day = 86400;

  const linked = users.filter((u) => u.telegram_user_id);
  const paused = users.filter((u) => u.paused);
  const active1h = users.filter((u) => u.last_user_msg_at && now - u.last_user_msg_at < hour);
  const active24h = users.filter((u) => u.last_user_msg_at && now - u.last_user_msg_at < day);
  const active7d = users.filter((u) => u.last_user_msg_at && now - u.last_user_msg_at < 7 * day);
  const overLimit = users.filter((u) => (u.lifetime_msgs_out ?? 0) >= (u.msg_limit ?? 100));

  const totalCost = users.reduce((s, u) => s + (u.lifetime_cost_usd ?? 0), 0);
  const totalMsgs = users.reduce((s, u) => s + (u.lifetime_msgs_out ?? 0), 0);
  const avgMsgsPerActive = active7d.length ? totalMsgs / active7d.length : 0;

  // Last message anywhere on system (bot or user)
  const lastActivity = users.reduce((max, u) => {
    const latest = Math.max(u.last_user_msg_at ?? 0, u.last_bot_msg_at ?? 0);
    return Math.max(max, latest);
  }, 0);

  return (
    <motion.div initial="hidden" animate="show" variants={stagger(0.06)}>
      <motion.div variants={fadeUp}>
        <div className="font-serif italic text-ink-soft">admin · system health</div>
        <h2 className="display text-4xl md:text-5xl mt-2">Live pulse.</h2>
        <p className="mt-3 text-ink-soft max-w-xl">
          Computed from the users table in real time. Last system activity:{" "}
          <span className="text-ink font-medium">{fmtRelativeTime(lastActivity)}</span>.
        </p>
      </motion.div>

      <motion.div variants={fadeUp} className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard label="active · last hour" value={fmtNum(active1h.length)} icon={<Activity size={14} />} delta={`${((active1h.length / Math.max(users.length, 1)) * 100).toFixed(1)}% of pool`} />
        <KpiCard label="active · last 24h" value={fmtNum(active24h.length)} icon={<Activity size={14} />} />
        <KpiCard label="active · last 7d" value={fmtNum(active7d.length)} icon={<Activity size={14} />} />
      </motion.div>

      <motion.div variants={fadeUp} className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard label="telegram linked" value={`${fmtNum(linked.length)} / ${users.length}`} icon={<Link2 size={14} />} delta={`${((linked.length / Math.max(users.length, 1)) * 100).toFixed(0)}% conversion`} />
        <KpiCard label="paused accounts" value={fmtNum(paused.length)} icon={<Pause size={14} />} delta={paused.length ? "investigate" : "none"} />
        <KpiCard label="over msg limit" value={fmtNum(overLimit.length)} icon={<MessageSquare size={14} />} delta={overLimit.length ? "review quotas" : "within budget"} />
      </motion.div>

      <motion.div variants={fadeUp} className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard label="lifetime msgs" value={fmtNum(totalMsgs)} icon={<MessageSquare size={14} />} />
        <KpiCard label="avg msgs / active user" value={avgMsgsPerActive.toFixed(1)} icon={<Users size={14} />} delta="last 7 days" />
        <KpiCard label="lifetime inference cost" value={fmtUsd(totalCost)} icon={<DollarSign size={14} />} tone="ink" />
      </motion.div>

      {/* Paused users table */}
      {paused.length > 0 && (
        <motion.div variants={fadeUp} className="mt-8 card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs text-ink-faint uppercase tracking-[0.14em]">attention needed</div>
              <h3 className="font-serif text-2xl mt-1">Paused accounts</h3>
            </div>
            <span className="text-sm text-ink-faint">{paused.length}</span>
          </div>
          <ul className="divide-y divide-[rgba(42,36,26,0.08)]">
            {paused.map((u) => (
              <li key={u.sub} className="flex items-center gap-4 py-2.5 text-sm">
                <div className="w-7 h-7 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center font-serif text-xs shrink-0">
                  {(u.first_name || u.email || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{u.first_name || "unnamed"}</div>
                  <div className="text-xs text-ink-faint truncate">{u.email}</div>
                </div>
                <div className="text-xs text-ink-faint">last seen {fmtRelativeTime(u.last_bot_msg_at)}</div>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* CloudWatch-derived system pulse — Lambda invocations / errors,
          tutor turn failures, memory pipeline, cron last-runs, OpenRouter
          routing, API Gateway. Pulled live on mount + manual refresh. */}
      <SystemPulse />
    </motion.div>
  );
}
